import { useState } from 'react'

// ── Mock Data ────────────────────────────────────────────────────────────────

const projects = [
  { id: 1, name: 'School Lunch Integration', status: 'In Progress', progress: 65, team: ['OB', 'GK'] },
  { id: 2, name: 'Fleet Watchdog v5', status: 'Review', progress: 90, team: ['OB'] },
  { id: 3, name: 'Parent Portal UX Refactor', status: 'Backlog', progress: 0, team: ['GK', 'ST'] },
  { id: 4, name: 'Azure SQL Migration', status: 'Completed', progress: 100, team: ['OB'] },
]

// ── Components ───────────────────────────────────────────────────────────────

export default function GlassTemplate2() {
  const [activeTab, setActiveTab] = useState('active')

  return (
    <div className="space-y-10 pb-20 animate-[fade-in-up_0.8s_ease-out]">
      {/* 1. Interactive Header Area */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
           <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Projects & Ops</h1>
           <p className="text-slate-500 font-medium">Manage cross-functional workflows and team velocity.</p>
        </div>
        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
           <button 
             onClick={() => setActiveTab('active')}
             className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${activeTab === 'active' ? 'bg-white shadow-md text-primary scale-105' : 'text-slate-400 hover:text-slate-600'}`}
           >
             Active
           </button>
           <button 
             onClick={() => setActiveTab('archive')}
             className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${activeTab === 'archive' ? 'bg-white shadow-md text-primary scale-105' : 'text-slate-400 hover:text-slate-600'}`}
           >
             Archive
           </button>
        </div>
      </div>

      {/* 2. Visual Project Board (Horizontal Scroll or Grid) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.map((p, i) => (
          <div 
            key={p.id}
            className="group bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 relative overflow-hidden opacity-0 animate-[fade-in-up_0.6s_ease-out_forwards]"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className="absolute top-0 right-0 p-8">
               <span className={`w-2 h-2 rounded-full inline-block ${p.progress === 100 ? 'bg-teal-500' : 'bg-amber-400'} shadow-[0_0_12px_rgba(0,0,0,0.1)]`} />
            </div>
            
            <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mb-8 group-hover:bg-primary group-hover:text-white transition-all duration-500 group-hover:rotate-12">
               <span className="material-symbols-outlined text-2xl">folder_managed</span>
            </div>

            <h3 className="font-bold text-xl mb-2 tracking-tight group-hover:text-primary transition-colors">{p.name}</h3>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-8">{p.status}</p>

            <div className="space-y-3">
               <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <span>Velocity</span>
                  <span className="text-slate-600">{p.progress}%</span>
               </div>
               <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-1000 ease-out" 
                    style={{ width: `${p.progress}%` }} 
                  />
               </div>
            </div>

            <div className="mt-8 pt-8 border-t border-slate-50 flex justify-between items-center">
               <div className="flex -space-x-3">
                  {p.team.map(member => (
                    <div key={member} className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-600 shadow-sm">{member}</div>
                  ))}
                  <div className="w-8 h-8 rounded-full bg-primary text-white border-2 border-white flex items-center justify-center text-[10px] font-bold shadow-sm cursor-pointer hover:scale-110 transition-transform">+</div>
               </div>
               <span className="material-symbols-outlined text-slate-300 group-hover:text-primary transition-colors cursor-pointer">arrow_forward_ios</span>
            </div>
          </div>
        ))}
      </div>

      {/* 3. Operational Timeline / Activity List */}
      <div className="bg-slate-950 rounded-[3rem] p-12 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        
        <div className="relative z-10 flex flex-col md:flex-row gap-16">
           <div className="md:w-1/3">
              <h3 className="text-3xl font-bold tracking-tight mb-4">Operations Log</h3>
              <p className="text-white/40 font-medium leading-relaxed">Real-time audit of system changes and user interactions across the ERP backbone.</p>
              <button className="mt-8 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/10 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all">Export Log</button>
           </div>
           
           <div className="flex-1 space-y-8">
              {[
                { time: '14:20', user: 'Ömer Beispiel', action: 'Deployed v2.4.0 to Staging', type: 'system' },
                { time: '12:05', user: 'Georgina K.', action: 'Updated 14 School Profiles', type: 'data' },
                { time: '09:45', user: 'System', action: 'Daily Backup Completed Successfully', type: 'backup' },
                { time: 'Yesterday', user: 'Ömer Beispiel', action: 'Restricted IT.Admin role access', type: 'security' },
              ].map((log, i) => (
                <div key={i} className="flex gap-6 group cursor-default">
                   <span className="text-[11px] font-mono text-white/30 pt-1 group-hover:text-primary transition-colors">{log.time}</span>
                   <div className="space-y-1">
                      <p className="text-[15px] font-bold text-white/90 group-hover:text-white transition-colors">{log.action}</p>
                      <p className="text-[11px] font-medium text-white/40 uppercase tracking-wider">{log.user} · <span className="text-white/20">{log.type}</span></p>
                   </div>
                </div>
              ))}
           </div>
        </div>
      </div>
    </div>
  )
}
